/* eslint-disable no-undef */
import React, {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {useLocalesMgr, useLocation} from 'coreHooks';

import {STORE_LOCATOR_ZOOM_LOCATION} from 'brand/app/pages/store-locator/config';
import {GoogleMapsLibraryStatus, GoogleMapsWrapper} from 'brandComponents/GoogleMapsWrapper';

import {useStoreLocatorContext} from '../../context/StoreLocatorContext';
import messages from '../../messages';
import StoreButtons from '../StoreButtons';
import StoreCardList from '../StoreCardList';
import StoreLocatorMap from '../StoreLocatorMap';
import StoreSearchInput from '../StoreSearchInput';

import {IStoreLocatorContainerProps, StoreLocatorViewMode} from './IStoreLocatorContainer';

import styles from './StoreLocatorContainer.module.scss';

const StoreLocatorContainer = ({mapApiKey}: IStoreLocatorContainerProps) => {
	const intl = useIntl();
	const location = useLocation();

	const {map, setCenter, stores, libraryStatus, setLibraryStatus} = useStoreLocatorContext();

	const [activeView, setActiveView] = useState<StoreLocatorViewMode>('map');
	const searchInputRef = useRef<HTMLInputElement>();
	const localesMgr = useLocalesMgr();
	const currentCountryCode = localesMgr.getCurrentCountry().getCountryCode();
	const languageCode = localesMgr.getCurrentLanguageCode();

	useEffect(() => {
		if (!map) {
			return;
		}

		const lat = Number(location.searchParams.get('lat')) || null;
		const lng = Number(location.searchParams.get('lng')) || null;
		const placeId = location.searchParams.get('placeId') || null;

		/**
		 * Using Place if exists (autocomplete)
		 */
		if (placeId) {
			const geocoder = new google.maps.Geocoder();
			geocoder.geocode(
				{
					placeId
				},
				(results, status) => {
					if (status == google.maps.GeocoderStatus.OK && results.length > 0) {
						map.fitBounds(results[0].geometry.viewport);
						map.setCenter(results[0].geometry.location);

						if (results[0].types.includes('country')) {
							// To reload stores
							map.setZoom(map.getZoom());
						} else {
							map.setZoom(STORE_LOCATOR_ZOOM_LOCATION);
						}

						searchInputRef.current.value = results[0].formatted_address;
					} else {
						/** Fallback on lat lng */
						if (lat && lng) {
							setCenter({lat, lng});
							map.setZoom(STORE_LOCATOR_ZOOM_LOCATION);
						}
					}
				},
			);

			return;
		}

		/**
		 * Using coords if exists (geolocation)
		 */
		if (lat && lng) {
			map.setCenter({lat, lng});
			map.setZoom(STORE_LOCATOR_ZOOM_LOCATION);

			return;
		}

		/**
		 * Using current country if no other options available
		 */
		if (currentCountryCode) {
			const geocoder = new google.maps.Geocoder();
			geocoder.geocode(
				{
					address: '',
					componentRestrictions: {
						country: currentCountryCode,
					},
				},
				(results, status) => {
					if (status == google.maps.GeocoderStatus.OK) {
						map.fitBounds(results[0].geometry.viewport);
						map.setZoom(map.getZoom());
					}
				},
			);
		}
	}, [map, currentCountryCode, location.search]);

	return (
		<>
			{libraryStatus === GoogleMapsLibraryStatus.FAILURE && (
				<div className={styles['library-failure']}>{intl.formatMessage(messages.libraryFailure)}</div>
			)}
			<GoogleMapsWrapper
				language={languageCode}
				apiKey={mapApiKey}
				libraries={['places', 'marker', 'geometry']}
				callback={(status) => {
					setLibraryStatus(status);
				}}
			>
				<div className={styles['store-locator-wrapper']}>
					<div className={styles['store-locator-filters']}>
						<StoreSearchInput
							searchInputRef={searchInputRef}
							inputPlaceholder={intl.formatMessage(messages.inputPlaceholder)}
						/>
						<StoreButtons onToggle={setActiveView} activeView={activeView} />
						<StoreCardList stores={stores} visible={activeView === 'list'} />
					</div>
					<StoreLocatorMap searchInputRef={searchInputRef} visible={activeView === 'map'} />
				</div>
			</GoogleMapsWrapper>
		</>
	);
};

export default StoreLocatorContainer;
